import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Award } from "lucide-react";
import { api } from "../lib/api";
import { useIdentity } from "../hooks/useIdentity";

/**
 * Floating welcome banner shown to identified users.
 *
 * UX:
 *   - CSS-driven slide-down animation on first render
 *   - Plays a soft 2-note chime via WebAudio once per session
 *   - Respects `prefers-reduced-motion`
 *   - Dismissable per session, reappears next visit
 */
export default function WelcomeBanner() {
  const { identity } = useIdentity();
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("welcome_dismissed") === "1",
  );
  const [closing, setClosing] = useState(false);
  const [balance, setBalance] = useState(null);
  const chimePlayedRef = useRef(false);

  useEffect(() => {
    if (!identity.email) return;
    let cancelled = false;
    api.get(`/loyalty/account/${encodeURIComponent(identity.email)}`)
      .then((r) => { if (!cancelled && r.data) setBalance(r.data.balance ?? 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [identity.email]);

  // Play chime once per session, right after the banner mounts
  useEffect(() => {
    if (!identity.firstName || dismissed || chimePlayedRef.current) return;
    chimePlayedRef.current = true;
    if (sessionStorage.getItem("welcome_chime_played") === "1") return;
    sessionStorage.setItem("welcome_chime_played", "1");
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!reduced) playChime();
  }, [identity.firstName, dismissed]);

  const dismiss = () => {
    setClosing(true);
    sessionStorage.setItem("welcome_dismissed", "1");
    setTimeout(() => setDismissed(true), 320);
  };

  if (!identity.firstName || dismissed) return null;

  return (
    <div
      data-testid="welcome-banner"
      aria-live="polite"
      className={`bg-gradient-to-r from-coral-dark via-coral to-pedablue text-white overflow-hidden ${closing ? "welcome-banner-closing" : "welcome-banner-enter"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-2 flex items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={14} className="shrink-0 animate-pulse" />
          <span className="truncate">
            Bienvenue <strong data-testid="welcome-firstname">{identity.firstName}</strong> ✨
            {balance !== null && balance > 0 && (
              <Link
                to="/fidelite"
                className="ml-3 inline-flex items-center gap-1 underline underline-offset-2 decoration-white/50 hover:decoration-white"
              >
                <Award size={12} /> {balance} orthopoints
              </Link>
            )}
          </span>
        </div>
        <button
          data-testid="welcome-banner-close"
          onClick={dismiss}
          className="shrink-0 opacity-80 hover:opacity-100 transition"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * Soft 2-note chime (C5 → E5) via WebAudio. ~600ms, peak gain 0.04 (very discrete).
 */
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const note = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.04, now + start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    note(523.25, 0,    0.35); // C5
    note(659.25, 0.18, 0.45); // E5 — major third (cheerful)
    setTimeout(() => ctx.close?.(), 1200);
  } catch {
    /* silently ignore */
  }
}
